import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../component/Navbar';
import './BillReset.css';

const API_BASE_URL = 'https://apiv2.labridge.in';

const getMessage = (error, fallback) =>
  error.response?.data?.message || error.response?.data?.error || error.message || fallback;

const displayValue = (value) => (value === null || value === undefined || value === '' ? '-' : value);

const formatAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })
    : '-';
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-IN');
};

function BillReset() {
  const navigate = useNavigate();
  const [orderId, setOrderId] = useState('');
  const [order, setOrder] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const resolvedOrderId = order?._id || order?.orderId || orderId.trim();
  const hasBillNumber = Boolean(order?.billNumber && String(order.billNumber).trim());
  const orderItems = useMemo(() => order?.orderItems || [], [order]);

  useEffect(() => {
    const savedLogin = localStorage.getItem('loginData');
    const adminData = savedLogin ? JSON.parse(savedLogin) : null;

    if (!adminData || !Array.isArray(adminData.access) || !adminData.access.includes('8')) {
      navigate('/login');
    }
  }, [navigate]);

  const fetchOrder = async (id = orderId, { keepSuccess = false } = {}) => {
    const trimmedOrderId = String(id || '').trim();
    if (!trimmedOrderId) {
      setOrder(null);
      setError('Enter an order ID to fetch booking details.');
      return null;
    }

    setIsFetching(true);
    setError('');
    if (!keepSuccess) setSuccess('');

    try {
      const response = await axios.get(`${API_BASE_URL}/admin/search/${encodeURIComponent(trimmedOrderId)}`);
      const result = response.data?.data || response.data;

      if (!result || typeof result !== 'object' || !result._id) {
        throw new Error(response.data?.message || 'No order was found for this order ID.');
      }

      setOrder(result);
      setOrderId(result._id || trimmedOrderId);
      return result;
    } catch (requestError) {
      setOrder(null);
      setError(getMessage(requestError, 'Unable to fetch order details.'));
      return null;
    } finally {
      setIsFetching(false);
    }
  };

  const handleFetch = (event) => {
    event.preventDefault();
    fetchOrder();
  };

  const resetBillNumber = async () => {
    setIsConfirming(false);
    setIsResetting(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API_BASE_URL}/bill/clear-billnumber`, {
        orderId: resolvedOrderId,
      });

      if (response.data?.success === false || response.data?.responseCode >= 400) {
        throw new Error(response.data?.message || 'Could not clear the bill number.');
      }

      const refreshedOrder = await fetchOrder(resolvedOrderId, { keepSuccess: true });
      if (!refreshedOrder) return;

      setSuccess(response.data?.message || 'Bill number cleared successfully. The order details have been refreshed.');
    } catch (requestError) {
      setError(getMessage(requestError, 'Unable to clear the bill number.'));
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="wrapper">
      <Navbar />
      <main className="container mt-5 bill-reset-page" id="content">
        <div className="mb-4">
          <h1 className="mb-1">Bill Reset</h1>
          <p className="text-muted mb-0">Review an order before clearing its bill number. This does not create a new bill.</p>
        </div>

        <form className="card mb-4" onSubmit={handleFetch}>
          <div className="card-body d-flex gap-2 flex-wrap">
            <label className="visually-hidden" htmlFor="bill-reset-order-id">Order ID</label>
            <input
              id="bill-reset-order-id"
              className="form-control bill-reset-order-input"
              type="text"
              placeholder="Enter order ID"
              value={orderId}
              onChange={(event) => setOrderId(event.target.value)}
              disabled={isFetching || isResetting}
            />
            <button className="btn btn-primary" type="submit" disabled={isFetching || isResetting}>
              {isFetching ? 'Fetching…' : 'Fetch order'}
            </button>
          </div>
        </form>

        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        {success && <div className="alert alert-success" role="status">{success}</div>}

        {order && (
          <section className="card" aria-labelledby="bill-reset-order-heading">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-4">
                <div>
                  <h2 id="bill-reset-order-heading" className="h4 mb-1">Order review</h2>
                  <span className="text-muted">Confirm the details below before resetting the bill number.</span>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => fetchOrder(resolvedOrderId)}
                  disabled={isFetching || isResetting}
                >
                  {isFetching ? 'Refreshing…' : 'Refresh details'}
                </button>
              </div>

              <div className="row g-3 mb-4">
                <OrderDetail label="Order ID" value={resolvedOrderId} />
                <OrderDetail label="Patient name" value={order.patientName} />
                <OrderDetail label="Mobile" value={order.mobile} />
                <OrderDetail label="Doctor name" value={order.doctorName} />
                <OrderDetail label="Current order status" value={order.status} />
                <OrderDetail label="Total amount" value={formatAmount(order.totalprice ?? order.totalAmount)} />
                <OrderDetail label="Booking date" value={formatDate(order.dateOrdered ?? order.bookingDate)} />
                <div className="col-12 col-sm-6 col-lg-4">
                  <div className="bill-reset-label">Current bill number</div>
                  <div className={`bill-reset-number ${hasBillNumber ? 'has-bill-number' : 'no-bill-number'}`}>
                    {hasBillNumber ? order.billNumber : 'No bill number assigned'}
                  </div>
                </div>
              </div>

              {orderItems.length > 0 && (
                <div className="mb-4">
                  <h3 className="h5 mb-3">Tests</h3>
                  <div className="table-responsive">
                    <table className="table table-sm table-striped align-middle mb-0">
                      <thead>
                        <tr>
                          <th scope="col">Test</th>
                          <th scope="col">Status</th>
                          <th scope="col">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderItems.map((item, index) => (
                          <tr key={item?._id || item?.orderItemId || index}>
                            <td>{displayValue(item?.testName || item?.name || item?.productName)}</td>
                            <td>{displayValue(item?.status)}</td>
                            <td>{formatAmount(item?.b2bAmount ?? item?.amount ?? item?.price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!hasBillNumber ? (
                <div className="alert alert-info mb-0" role="status">
                  This order does not currently have a bill number assigned.
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setIsConfirming(true)}
                  disabled={isFetching || isResetting}
                >
                  {isResetting ? 'Clearing bill number…' : 'Reset bill number'}
                </button>
              )}
            </div>
          </section>
        )}

        {isConfirming && (
          <div className="bill-reset-dialog-backdrop" role="presentation">
            <div className="bill-reset-dialog" role="dialog" aria-modal="true" aria-labelledby="bill-reset-confirm-title">
              <h2 id="bill-reset-confirm-title" className="h5">Clear bill number?</h2>
              <p>Are you sure you want to clear the bill number for this order?</p>
              <p className="text-muted small mb-4">Order ID: {resolvedOrderId}</p>
              <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setIsConfirming(false)} disabled={isResetting}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={resetBillNumber} disabled={isResetting}>
                  {isResetting ? 'Clearing…' : 'Yes, clear bill number'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function OrderDetail({ label, value }) {
  return (
    <div className="col-12 col-sm-6 col-lg-4">
      <div className="bill-reset-label">{label}</div>
      <div>{displayValue(value)}</div>
    </div>
  );
}

export default BillReset;
